// ** React Imports
import { ReactNode, useState, useEffect } from 'react';

// ** Next Imports
import { useRouter } from 'next/router';

// ** Types
import type { ACLObj, AppAbility } from 'src/configs/acl';

// ** Context Imports
import { AbilityContext } from 'src/layouts/components/acl/Can';

// ** Config Import
import { buildAbilityFor } from 'src/configs/acl';

// ** Component Import
import NotAuthorized from 'src/pages/401';
import BlankLayout from 'src/@core/layouts/BlankLayout';

// ** Hooks
import { useAuth } from 'react-oidc-context';

interface AclGuardProps {
  children: ReactNode;
  guestGuard: boolean;
  aclAbilities: ACLObj;
}

const AclGuard = (props: AclGuardProps) => {
  // ** Props
  const { aclAbilities, children, guestGuard } = props;

  // ** Hooks
  const auth = useAuth();
  const router = useRouter();

  const [ability, setAbility] = useState<AppAbility | undefined>(() =>
    auth.user ? buildAbilityFor('admin', aclAbilities.subject) : undefined
  );

  useEffect(() => {
    if (auth.user) {
      setAbility(buildAbilityFor('admin', aclAbilities.subject));
    }
  }, [auth.user, aclAbilities.subject]);

  // If guestGuard is true and user is not logged in or its an error page, render the page without checking access
  if (
    guestGuard ||
    router.route === '/404' ||
    router.route === '/500' ||
    router.route === '/503' ||
    router.route === '/'
  ) {
    return <div>{children}</div>;
  }

  // Check the access of current user and render pages
  if (ability && ability.can(aclAbilities.action, aclAbilities.subject)) {
    return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
  }

  // Render Not Authorized component if the current user has limited access
  return (
    <BlankLayout>
      <NotAuthorized />
    </BlankLayout>
  );
};

export default AclGuard;
